import { chain, router } from '@leprecoins/effector';
import { createForm } from '@leprecoins/effector-forms';
import { sample } from 'effector';
import { createBlockCashdeskModel } from 'features/block-cashdesk';
import { createDeleteCashdeskModel } from 'features/delete-cashdesk';
import { createDepositCashdeskModel } from 'features/deposit-cashdesk';
import { createUnblockCashdeskModel } from 'features/unblock-cashdesk';
import { createWithdrawCashdeskModel } from 'features/withdraw-cashdesk';
import {
	CASHDESK_NAME_MIN_LENGTH,
	CASHDESK_NOTE_MAX_LENGTH,
	CASHDESK_FAST_DEPOSITS_MAX_LENGTH,
} from 'entities/cashdesk';
import { createEditCashdeskMutation, getCashiersQuery } from 'shared/api';
import {
	notEmptyRule,
	minLengthRule,
	maxLengthRule,
	notify,
	arrayIncreasingRule,
	maxLengthArrayRule,
} from 'shared/lib';
import { authorizedCashdeskRoute } from 'shared/routing';
import { cashdeskPageModel } from './cashdesk-page-model';

const editCashdeskMutation = createEditCashdeskMutation();

const withdrawCashdeskModel = createWithdrawCashdeskModel();

const depositCashdeskModel = createDepositCashdeskModel();

const blockCashdeskModel = createBlockCashdeskModel();

const unblockCashdeskModel = createUnblockCashdeskModel();

const deleteCashdeskModel = createDeleteCashdeskModel();

const form = createForm({
	fields: {
		name: {
			init: '',
			rules: [
				notEmptyRule,
				minLengthRule(CASHDESK_NAME_MIN_LENGTH),
				maxLengthRule(CASHDESK_NOTE_MAX_LENGTH),
			],
		},
		account: {
			init: '',
		},
		currency: {
			init: '',
		},
		balance: {
			init: '',
		},
		cashierIds: {
			init: [] as number[],
		},
		fastDeposits: {
			init: [] as number[],
			rules: [maxLengthArrayRule(CASHDESK_FAST_DEPOSITS_MAX_LENGTH), arrayIncreasingRule],
		},
		note: {
			init: '',
			rules: [maxLengthRule(CASHDESK_NOTE_MAX_LENGTH)],
		},
	},
	validateOn: ['submit'],
	resetErrorsOnChange: true,
});

sample({
	clock: cashdeskPageModel.getCashdeskQuery.finished.success,
	fn: ({ result }) => ({
		name: result.name,
		account: result.account.name,
		currency: result.currency,
		balance: result.balance.toFixed(2),
		cashierIds: result.cashiers.map((cashier) => cashier.id),
		note: result.note ?? '',
		fastDeposits: result.fastDeposits,
	}),
	target: [form.setInitialValues, form.setValues],
});

sample({
	clock: form.submitted,
	source: { values: form.$values, cashdesk: cashdeskPageModel.$cashdesk },
	filter: ({ cashdesk }) => cashdesk !== null,
	fn: ({ values, cashdesk }) => ({
		id: cashdesk!.id,
		...values,
	}),
	target: editCashdeskMutation.start,
});

notify(editCashdeskMutation.finished.success, 'cashdesk-page.edit-success');

chain(editCashdeskMutation.finished.success, getCashiersQuery.reset);

sample({
	clock: [
		depositCashdeskModel.depositCashdeskMutation.finished.success,
		withdrawCashdeskModel.withdrawCashdeskMutation.finished.success,
		editCashdeskMutation.finished.success,
		blockCashdeskModel.mutation.finished.success,
		unblockCashdeskModel.mutation.finished.success,
	],
	source: cashdeskPageModel.$cashdesk,
	filter: Boolean,
	fn: ({ id }) => id,
	target: cashdeskPageModel.getCashdeskQuery.start,
});

chain(deleteCashdeskModel.mutation.finished.success, router.back);

sample({
	clock: authorizedCashdeskRoute.closed,
	target: [form.reset, editCashdeskMutation.reset],
});

export const cashdeskInfoModel = {
	form,
	editCashdeskMutation,
	withdrawCashdeskModel,
	depositCashdeskModel,
	blockCashdeskModel,
	unblockCashdeskModel,
	deleteCashdeskModel,
};
